import { addDays, type ISODate } from "@/engine/date";
import type { ExecutionPriority, ExecutionStatus, ExecutionTask, Project, Schedule } from "@/engine/types";
import { rollupGates, type GateRollup } from "@/store/gate-ops";
import { isOverdue, PRIORITY_LABEL, PRIORITY_ORDER, STATUS_LABEL, STATUS_ORDER, undecided } from "@/store/execution-task-ops";

/** Everything a filter/group/sort needs besides the tasks themselves. */
export interface TaskQueryContext {
  project: Pick<Project, "tasks" | "planner">;
  schedule: Schedule | null;
  today: ISODate;
}

export type DueWindow = "any" | "overdue" | "today" | "week" | "none" | "scheduled";

export interface TaskFilter {
  query: string;
  /** "unset" = 상태 미정 */
  statuses: Array<ExecutionStatus | "unset">;
  priorities: Array<ExecutionPriority | "unset">;
  /** "" = 담당 미정 */
  assignees: string[];
  /** "" = 버킷 없음 */
  bucketIds: string[];
  labelIds: string[];
  /** schedule task ids; a WBS id also matches tasks linked to any of its descendants */
  linkedTaskIds: string[];
  due: DueWindow;
  /** only tasks with at least one undecided field */
  undecidedOnly: boolean;
  hideDone: boolean;
}

export function emptyFilter(): TaskFilter {
  return { query: "", statuses: [], priorities: [], assignees: [], bucketIds: [], labelIds: [], linkedTaskIds: [], due: "any", undecidedOnly: false, hideDone: true };
}

/** True when the filter differs from defaults. hideDone is a standing preference (default on). */
export function isFilterActive(f: TaskFilter): boolean {
  return !!(f.query || f.statuses.length || f.priorities.length || f.assignees.length || f.bucketIds.length || f.labelIds.length || f.linkedTaskIds.length || f.due !== "any" || f.undecidedOnly);
}

export type GroupKey = "none" | "bucket" | "status" | "priority" | "assignee" | "scope" | "scopeRoot" | "due" | "label";
export type SortKey = "manual" | "due" | "priority" | "title" | "updated" | "created";

export const GROUP_LABEL: Record<GroupKey, string> = {
  none: "그룹 없음",
  bucket: "버킷",
  status: "상태",
  priority: "우선순위",
  assignee: "담당자",
  scope: "WBS / 작업",
  scopeRoot: "WBS / 최상위",
  due: "마감",
  label: "라벨",
};

export function isScopeGroupKey(key: GroupKey): boolean {
  return key === "scope" || key === "scopeRoot";
}

export const SORT_LABEL: Record<SortKey, string> = {
  manual: "수동 순서",
  due: "마감일",
  priority: "우선순위",
  title: "제목",
  updated: "최근 수정",
  created: "만든 순서",
};

/** 목록 정렬 메뉴. 보드는 칸 순서를 쓰므로 여기 두지 않는다. */
export const LIST_SORT_KEYS: SortKey[] = ["due", "priority", "title", "updated", "created"];

/* ---------------------------------------------------------------- scope (WBS / Activity) */

export type ScopeKind = "wbs" | "activity" | "milestone" | "missing";

export interface ScopeRef {
  id: string;
  wbs: string;
  name: string;
  kind: ScopeKind;
  start?: ISODate;
  finish?: ISODate;
}

export function scopeRef(id: string, ctx: TaskQueryContext): ScopeRef {
  const task = ctx.project.tasks.find((t) => t.id === id);
  const s = ctx.schedule?.tasks.get(id);
  if (!task) return { id, wbs: "", name: "삭제된 작업", kind: "missing" };
  const kind: ScopeKind = s?.isSummary ? "wbs" : s?.isMilestone || task.duration === 0 ? "milestone" : "activity";
  return { id, wbs: s?.wbs ?? "", name: task.name || "이름 없는 작업", kind, start: s?.start, finish: s?.finish };
}

export function scopeRefs(task: ExecutionTask, ctx: TaskQueryContext): ScopeRef[] {
  return task.linkedTaskIds.map((id) => scopeRef(id, ctx));
}

/** Format the full path from root to node (e.g. "1 착수 › 1.1 현황 분석 › 1.1.2 AS-IS 프로세스 정리") */
export function scopeBreadcrumb(id: string, ctx: TaskQueryContext): string {
  const self = scopeRef(id, ctx);
  if (self.kind === "missing") return self.name;
  const ancs = ancestorsOf(id, ctx.schedule).reverse();
  if (!ancs.length) return `${self.wbs} ${self.name}`.trim();
  const path = ancs.map((aid) => {
    const ref = scopeRef(aid, ctx);
    return `${ref.wbs} ${ref.name}`.trim();
  });
  path.push(`${self.wbs} ${self.name}`.trim());
  return path.join(" › ");
}

export const SCOPE_KIND_LABEL: Record<ScopeKind, string> = { wbs: "WBS", activity: "작업", milestone: "마일스톤", missing: "없음" };

/** Schedule ancestors of a task id (nearest first), from the schedule's depth information. */
export function ancestorsOf(id: string, schedule: Schedule | null): string[] {
  if (!schedule) return [];
  const out: string[] = [];
  const idx = schedule.order.indexOf(id);
  if (idx < 0) return out;
  let depth = schedule.tasks.get(id)!.depth;
  for (let i = idx - 1; i >= 0 && depth > 0; i--) {
    const t = schedule.tasks.get(schedule.order[i]!)!;
    if (t.depth < depth) {
      out.push(t.id);
      depth = t.depth;
    }
  }
  return out;
}

/** Document-root ancestor; the id itself when already at depth 0 or unknown. */
export function rootScopeId(id: string, schedule: Schedule | null): string {
  const ancs = ancestorsOf(id, schedule);
  return ancs.at(-1) ?? id;
}

/** True when the task links to `scopeId` or to anything underneath it. */
export function linkedUnder(task: ExecutionTask, scopeId: string, schedule: Schedule | null): boolean {
  if (task.linkedTaskIds.includes(scopeId)) return true;
  return task.linkedTaskIds.some((id) => ancestorsOf(id, schedule).includes(scopeId));
}

/** Execution tasks attached to a schedule node or its descendants. */
export function tasksLinkedTo(scopeId: string, tasks: ExecutionTask[], schedule: Schedule | null): ExecutionTask[] {
  return tasks.filter((t) => linkedUnder(t, scopeId, schedule));
}

export function countLinkedExecution(
  scopeId: string,
  tasks: readonly ExecutionTask[],
  schedule: Schedule | null,
): { done: number; total: number } & GateRollup {
  let done = 0;
  let total = 0;
  const linked: ExecutionTask[] = [];
  for (const t of tasks) {
    if (!linkedUnder(t, scopeId, schedule)) continue;
    total += 1;
    linked.push(t);
    if (t.status === "done") done += 1;
  }
  return { done, total, ...rollupGates(linked) };
}

/* ---------------------------------------------------------------- due windows */

export type DueBucket = "overdue" | "today" | "week" | "later" | "none";

export const DUE_LABEL: Record<DueBucket, string> = { overdue: "지연", today: "오늘", week: "이번 주", later: "나중", none: "마감 없음" };

export function dueBucket(task: ExecutionTask, today: ISODate): DueBucket {
  if (!task.due) return "none";
  if (task.status === "done") return task.due < today ? "later" : task.due === today ? "today" : "later";
  if (task.due < today) return "overdue";
  if (task.due === today) return "today";
  if (task.due <= addDays(today, 7)) return "week";
  return "later";
}

/* ---------------------------------------------------------------- filter / sort / group */

function tokens(q: string): string[] {
  return q.toLowerCase().split(/\s+/).filter(Boolean);
}

export function matchesQuery(task: ExecutionTask, q: string, ctx: TaskQueryContext): boolean {
  const toks = tokens(q);
  if (!toks.length) return true;
  const labels = ctx.project.planner.labels.filter((l) => task.labelIds.includes(l.id)).map((l) => l.name);
  const bucket = ctx.project.planner.buckets.find((b) => b.id === task.bucketId)?.name ?? "";
  const scopes = scopeRefs(task, ctx).map((s) => `${s.wbs} ${s.name}`);
  const hay = [task.title, task.notes ?? "", bucket, ...task.assignees, ...labels, ...scopes, ...task.checklist.map((c) => c.text)].join(" ").toLowerCase();
  return toks.every((t) => hay.includes(t));
}

export function applyFilter(tasks: ExecutionTask[], f: TaskFilter, ctx: TaskQueryContext): ExecutionTask[] {
  return tasks.filter((t) => {
    if (f.hideDone && t.status === "done" && !f.statuses.includes("done")) return false;
    if (f.statuses.length && !f.statuses.includes(t.status ?? "unset")) return false;
    if (f.priorities.length && !f.priorities.includes(t.priority ?? "unset")) return false;
    if (f.assignees.length && !(t.assignees.length ? t.assignees.some((a) => f.assignees.includes(a)) : f.assignees.includes(""))) return false;
    if (f.bucketIds.length && !f.bucketIds.includes(t.bucketId ?? "")) return false;
    if (f.labelIds.length && !t.labelIds.some((l) => f.labelIds.includes(l))) return false;
    if (f.linkedTaskIds.length && !f.linkedTaskIds.some((id) => linkedUnder(t, id, ctx.schedule))) return false;
    if (f.due !== "any") {
      const b = dueBucket(t, ctx.today);
      if (f.due === "overdue" && b !== "overdue") return false;
      if (f.due === "today" && b !== "today") return false;
      if (f.due === "week" && !(b === "today" || b === "week" || b === "overdue")) return false;
      if (f.due === "none" && (t.due || t.start)) return false;
      if (f.due === "scheduled" && !t.due && !t.start) return false;
    }
    if (f.undecidedOnly && undecided(t).length === 0) return false;
    if (!matchesQuery(t, f.query, ctx)) return false;
    return true;
  });
}

const priorityRank = (p?: ExecutionPriority) => (p ? PRIORITY_ORDER.indexOf(p) : PRIORITY_ORDER.length);
const statusRank = (s?: ExecutionStatus) => (s ? STATUS_ORDER.indexOf(s) : -1);

export function sortTasks(tasks: ExecutionTask[], key: SortKey): ExecutionTask[] {
  const out = [...tasks];
  const byManual = (a: ExecutionTask, b: ExecutionTask) => a.order - b.order || a.createdAt.localeCompare(b.createdAt);
  switch (key) {
    case "due":
      out.sort((a, b) => (a.due ?? "9999").localeCompare(b.due ?? "9999") || byManual(a, b));
      break;
    case "priority":
      out.sort((a, b) => priorityRank(a.priority) - priorityRank(b.priority) || byManual(a, b));
      break;
    case "title":
      out.sort((a, b) => a.title.localeCompare(b.title, "ko") || byManual(a, b));
      break;
    case "updated":
      out.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
      break;
    case "created":
      out.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
      break;
    default:
      out.sort(byManual);
  }
  return out;
}

export interface TaskGroup {
  /** stable key for the column/section; "" means "none / undecided" */
  key: string;
  label: string;
  tasks: ExecutionTask[];
  /** patch that puts a task into this group (drag-and-drop target) */
  patch: Partial<ExecutionTask> | null;
}

export function groupTasks(tasks: ExecutionTask[], key: GroupKey, ctx: TaskQueryContext): TaskGroup[] {
  if (key === "none") return [{ key: "all", label: "모든 작업", tasks, patch: null }];
  const groups: TaskGroup[] = [];
  const push = (k: string, label: string, patch: Partial<ExecutionTask> | null) => {
    const g: TaskGroup = { key: k, label, tasks: [], patch };
    groups.push(g);
    return g;
  };
  if (key === "bucket") {
    const map = new Map<string, TaskGroup>();
    for (const b of ctx.project.planner.buckets) map.set(b.id, push(b.id, b.name, { bucketId: b.id }));
    const none = push("", "버킷 없음", { bucketId: undefined });
    for (const t of tasks) (map.get(t.bucketId ?? "") ?? none).tasks.push(t);
    return groups.filter((g) => g.key !== "" || g.tasks.length);
  }
  if (key === "status") {
    const map = new Map<string, TaskGroup>();
    map.set("", push("", "상태 미정", { status: undefined }));
    for (const s of STATUS_ORDER) map.set(s, push(s, STATUS_LABEL[s], { status: s }));
    for (const t of tasks) map.get(t.status ?? "")!.tasks.push(t);
    return groups;
  }
  if (key === "priority") {
    const map = new Map<string, TaskGroup>();
    for (const p of PRIORITY_ORDER) map.set(p, push(p, PRIORITY_LABEL[p], { priority: p }));
    map.set("", push("", "우선순위 미정", { priority: undefined }));
    for (const t of tasks) map.get(t.priority ?? "")!.tasks.push(t);
    return groups;
  }
  if (key === "assignee") {
    const names = new Set<string>();
    for (const t of tasks) for (const a of t.assignees) names.add(a);
    const map = new Map<string, TaskGroup>();
    for (const n of [...names].sort((a, b) => a.localeCompare(b, "ko"))) map.set(n, push(n, n, { assignees: [n] }));
    const none = push("", "담당 미정", { assignees: [] });
    for (const t of tasks) {
      if (!t.assignees.length) none.tasks.push(t);
      else for (const a of t.assignees) map.get(a)!.tasks.push(t);
    }
    return groups;
  }
  if (key === "label") {
    const map = new Map<string, TaskGroup>();
    for (const l of ctx.project.planner.labels) map.set(l.id, push(l.id, l.name, { labelIds: [l.id] }));
    const none = push("", "라벨 없음", { labelIds: [] });
    for (const t of tasks) {
      if (!t.labelIds.length) none.tasks.push(t);
      else for (const l of t.labelIds) map.get(l)?.tasks.push(t);
    }
    return groups.filter((g) => g.tasks.length || g.key !== "");
  }
  if (key === "due") {
    const order: DueBucket[] = ["overdue", "today", "week", "later", "none"];
    const map = new Map<string, TaskGroup>();
    for (const d of order) map.set(d, push(d, DUE_LABEL[d], d === "none" ? { due: undefined } : null));
    for (const t of tasks) map.get(dueBucket(t, ctx.today))!.tasks.push(t);
    return groups;
  }
  // scope: group by the direct linked WBS/activity node with full breadcrumb path
  // scopeRoot: roll every link up to its depth-0 ancestor (e.g. 2.1 → 2)
  const rootsOnly = key === "scopeRoot";
  const map = new Map<string, TaskGroup>();
  const order = ctx.schedule?.order ?? ctx.project.tasks.map((t) => t.id);
  for (const id of order) {
    if (rootsOnly) {
      const st = ctx.schedule?.tasks.get(id);
      if (st ? st.depth !== 0 : ancestorsOf(id, ctx.schedule).length > 0) continue;
      const ref = scopeRef(id, ctx);
      map.set(id, push(id, `${ref.wbs} ${ref.name}`.trim(), { linkedTaskIds: [id] }));
      continue;
    }
    map.set(id, push(id, scopeBreadcrumb(id, ctx), { linkedTaskIds: [id] }));
  }
  const none = push("", "범위 미정", { linkedTaskIds: [] });
  for (const t of tasks) {
    if (!t.linkedTaskIds.length) {
      none.tasks.push(t);
      continue;
    }
    let placed = false;
    const seen = new Set<string>();
    for (const id of t.linkedTaskIds) {
      const groupId = rootsOnly ? rootScopeId(id, ctx.schedule) : id;
      if (seen.has(groupId)) continue;
      seen.add(groupId);
      const g = map.get(groupId);
      if (g) {
        g.tasks.push(t);
        placed = true;
      }
    }
    if (!placed) none.tasks.push(t);
  }
  return groups.filter((g) => g.tasks.length || g.key !== "");
}

/* ---------------------------------------------------------------- analytics */

export interface Count {
  key: string;
  label: string;
  value: number;
}

export interface TaskSummary {
  total: number;
  done: number;
  inProgress: number;
  blocked: number;
  notStarted: number;
  unsetStatus: number;
  overdue: number;
  dueSoon: number;
  unscheduled: number;
  unassigned: number;
  unscoped: number;
  byStatus: Count[];
  byPriority: Count[];
  byBucket: Count[];
  byAssignee: Count[];
  byLabel: Count[];
  byScope: Count[];
  /** completed per ISO week (last 8 weeks), oldest first */
  completedByWeek: Array<{ weekStart: ISODate; value: number }>;
}

export function summarize(tasks: ExecutionTask[], ctx: TaskQueryContext): TaskSummary {
  const s = (st?: ExecutionStatus) => tasks.filter((t) => t.status === st).length;
  const counts = (groups: TaskGroup[]): Count[] => groups.map((g) => ({ key: g.key, label: g.label, value: g.tasks.length }));
  const weekStart = (iso: ISODate) => {
    const d = new Date(`${iso}T00:00:00Z`);
    const dow = (d.getUTCDay() + 6) % 7; // Monday = 0
    return addDays(iso, -dow);
  };
  const thisWeek = weekStart(ctx.today);
  const weeks: Array<{ weekStart: ISODate; value: number }> = [];
  for (let i = 7; i >= 0; i--) weeks.push({ weekStart: addDays(thisWeek, -7 * i), value: 0 });
  for (const t of tasks) {
    if (t.status !== "done" || !t.completedAt) continue;
    const ws = weekStart(t.completedAt.slice(0, 10));
    const w = weeks.find((x) => x.weekStart === ws);
    if (w) w.value++;
  }
  return {
    total: tasks.length,
    done: s("done"),
    inProgress: s("inProgress"),
    blocked: s("blocked"),
    notStarted: s("notStarted"),
    unsetStatus: s(undefined),
    overdue: tasks.filter((t) => isOverdue(t, ctx.today)).length,
    dueSoon: tasks.filter((t) => ["today", "week"].includes(dueBucket(t, ctx.today))).length,
    unscheduled: tasks.filter((t) => !t.start && !t.due).length,
    unassigned: tasks.filter((t) => !t.assignees.length).length,
    unscoped: tasks.filter((t) => !t.linkedTaskIds.length).length,
    byStatus: counts(groupTasks(tasks, "status", ctx)),
    byPriority: counts(groupTasks(tasks, "priority", ctx)),
    byBucket: counts(groupTasks(tasks, "bucket", ctx)),
    byAssignee: counts(groupTasks(tasks, "assignee", ctx)),
    byLabel: counts(groupTasks(tasks, "label", ctx)),
    byScope: counts(groupTasks(tasks, "scopeRoot", ctx)),
    completedByWeek: weeks,
  };
}
